import _ from 'lodash';
import moment from 'moment-timezone';
import { sqDurationStore, sqTrendStore } from '@/core/core.stores';
import { getMaxSeriesPixels } from '@/core/utilities';
import { NUMBER_CONVERSIONS } from '@/main/app.constants';
import { getDefaultCompareViewPropertyValue } from '@/services/compareView.utilities';
import { conditionFormula, findCapsulesQueryInterval } from '@/tools/manualCondition/conditionFormula.service';
import { getDurationDisplayRange } from '@/trendData/duration.utilities';
import { ITEM_CHILDREN_TYPES, PREVIEW_ID, PREVIEW_PREFIX } from '@/trendData/trendData.constants';
import { SPIKECATCHER_PER_PIXEL } from '@/utilities/formula.constants';
import { getMSPerPixelWidth } from '@/utilities/utilities';
import type { Cursor } from 'baobab';
import type { RangeExport } from '@/trendData/duration.types';
import type { CapsuleToAdd } from '@/trendData/trendData.types';
import type { TrendCapsuleStoreItem } from '@/trendData/trendSeries.store';
import type { Capsule } from '@/utilities/datetime.constants';

/**
 * Finds the minimum timestamp in the associated capsule.
 *
 * @param {Object} itemCursor - Cursor to the item
 *
 * @return {Number} The timestamp in milliseconds of the minimum.
 */
export function getMinTime(itemCursor: Cursor): number | null {
  if (itemCursor.get('childType') === ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE) {
    return itemCursor.get('startTime');
  } else {
    return null;
  }
}

/**
 * Prepares the formula for fetching signal segments used in the capsule time view.
 * @param seriesId - The series id
 * @param capsules - The set of capsules for which we calculate the query interval
 * @param chartWidth - The chart width
 * @param longestCapsuleDuration - The longest capsule duration
 * @returns formula and parameters for retrieving the samples of the signal segments
 */
export function buildSignalSegmentsFormula(
  seriesId: string,
  capsules: Capsule[],
  chartWidth: number,
  longestCapsuleDuration: number,
  stats?: string,
): {
  formula: string;
  limit: number;
  capsulesQueryIntervals: Capsule[];
  range: RangeExport;
} {
  const numPixels = Math.min(chartWidth, getMaxSeriesPixels());
  const offset = sqTrendStore.capsuleTimeOffsets;
  const queryDataOutsideCapsules = sqTrendStore.dimDataOutsideCapsules;

  const capsulesQueryIntervals = findCapsulesQueryInterval(
    seriesId,
    capsules,
    offset,
    longestCapsuleDuration,
    sqDurationStore.getState().displayRange.end,
    queryDataOutsideCapsules,
  );

  if (_.isEmpty(capsulesQueryIntervals)) {
    return {
      formula: '',
      limit: 0,
      capsulesQueryIntervals: [],
      range: { start: moment(), end: moment(), duration: moment.duration(0) },
    };
  }

  const maxCapsuleDuration = _.maxBy(capsulesQueryIntervals, (capsule) => capsule.endTime - capsule.startTime);
  const laneWidthMillis = getMSPerPixelWidth(maxCapsuleDuration!.endTime - maxCapsuleDuration!.startTime, numPixels);
  const laneWidth = `${NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND * laneWidthMillis}ns`;
  const formulaRangeStart = moment.utc(_.minBy(capsulesQueryIntervals, 'startTime')!.startTime);
  const formulaRangeEnd = moment.utc(_.maxBy(capsulesQueryIntervals, 'endTime')!.endTime);
  const range: RangeExport = {
    start: formulaRangeStart,
    end: formulaRangeEnd,
    duration: moment.duration(formulaRangeEnd.valueOf() - formulaRangeStart.valueOf()),
  };

  const conditionFormulaValue = conditionFormula(capsulesQueryIntervals);
  const downSampleFormula = sqTrendStore.buildDownsampleFormula(laneWidth, seriesId, conditionFormulaValue, stats);
  const summarizeFormula = sqTrendStore.buildSummarizeFormula(seriesId);

  const formula = `$series${summarizeFormula}${downSampleFormula}`;
  const limit = capsules.length * chartWidth * SPIKECATCHER_PER_PIXEL;

  return { formula, limit, capsulesQueryIntervals, range };
}

/**
 * Creates a unique id for capsule series
 *
 * @param {string} capsuleId - The id of the capsule
 * @param {string} signalId - The id of the signal
 */
export function createUniqueCapsuleSeriesId(capsuleId: string, signalId: string): string {
  return `${capsuleId}${signalId}`;
}

/** The fields of a capsule series that come from its capsule and the capsule time view, not from its signal */
export interface CapsuleSeriesFields {
  id: string;
  capsuleId: string;
  interestId: string;
  isChildOf: string;
  childType: ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE;
  otherChildrenOf: string[];
  startTime: number | undefined;
  endTime: number;
  duration: number | undefined;
  column: string;
  colorBy: string;
}

/**
 * Builds the fields a capsule series takes from its capsule and the capsule time view. A capsule that has not
 * ended uses `alternateEndTime`, because a series with no end time draws no line (CRAB-18916).
 */
export function deriveCapsuleSeriesFields(
  capsule: Partial<Pick<TrendCapsuleStoreItem, 'startTime' | 'endTime' | 'duration' | 'isChildOf' | 'properties'>> & {
    id: string;
  },
  interestId: string,
  view: { separateByProperty?: string; colorByProperty?: string; alternateEndTime: number },
): CapsuleSeriesFields {
  const propertyValue = (property: string | undefined) => {
    const value = property ? capsule.properties?.[property] : undefined;
    return getDefaultCompareViewPropertyValue(value == null ? undefined : String(value));
  };

  return {
    id: createUniqueCapsuleSeriesId(capsule.id, interestId),
    capsuleId: capsule.id,
    interestId,
    isChildOf: interestId,
    childType: ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE,
    otherChildrenOf: [capsule.isChildOf, capsule.id].filter((id): id is string => !!id),
    startTime: capsule.startTime ?? undefined,
    endTime: capsule.endTime ?? view.alternateEndTime,
    duration: capsule.duration,
    column: propertyValue(view.separateByProperty),
    colorBy: propertyValue(view.colorByProperty),
  };
}

/**
 * The end time to use for capsule series whose capsule has not ended: now, capped at the display range end.
 */
export function getAlternateEndTime(): number {
  const displayRangeEnd = getDurationDisplayRange().end.valueOf();
  const now = Date.now();

  return now < displayRangeEnd ? now : displayRangeEnd;
}

/**
 * Finds the capsule series affected by each uncertain-capsule replacement and the new id each one
 * takes. Shared by the series store's sync and the dispatch site, so the flux rename and the
 * runtime re-keying cannot drift apart.
 */
export function deriveUncertainCapsuleSeriesRenames(
  seriesItems: Array<{ id: string; capsuleId?: string; interestId?: string }>,
  replacements: Partial<Record<string, CapsuleToAdd>>,
): Array<{ seriesId: string; newSeriesId: string; newCapsule: CapsuleToAdd }> {
  return Object.entries(replacements).flatMap(([oldCapsuleId, newCapsule]) =>
    newCapsule
      ? seriesItems
          .filter((item) => item.capsuleId === oldCapsuleId)
          .map((series) => ({
            seriesId: series.id,
            newSeriesId: createUniqueCapsuleSeriesId(newCapsule.id, series.interestId ?? ''),
            newCapsule,
          }))
      : [],
  );
}

/**
 * The id a preview series is stored under. Preview ids are prefixed so they cannot collide with the
 * real item they preview, except for the standalone preview which has no underlying item.
 */
export function getPreviewSeriesId(id?: string): string {
  const rawId = id ?? PREVIEW_ID;
  if (rawId === PREVIEW_ID) {
    return PREVIEW_ID;
  }
  return rawId.startsWith(PREVIEW_PREFIX) ? rawId : `${PREVIEW_PREFIX}${rawId}`;
}
